/**
 * Kaskada virov metapodatkov (T3 §3).
 *
 * ```
 * 1. AzuraCast SSE        (če zaznano)   → push, brez pollinga
 * 2. Icecast status-json  (če CORS OK)   → poll 15 s
 * 3. HLS TextTrack        (če HLS)       → cuechange              ← faza 6
 * 4. ICY prek Workerja                    → univerzalna rezerva
 * ```
 *
 * **Sposobnost postaje se ugotovi enkrat in shrani** (T2 §6) — preverjanje ob
 * vsakem predvajanju je potrata, ki jo uporabnik plača z zamikom pred prvim
 * naslovom skladbe.
 *
 * ⚠️ Zapis je predpomnilnik odločitve, ne dejstvo: postaja lahko zamenja
 * ponudnika. Zato dve varovalki — postaran `metaCheckedAt` (30 dni, za `none`
 * 24 h) in `REDETECT_AFTER_FAILURES` zaporednih neuspehov, oba poženeta kaskado
 * znova.
 *
 * 🔴 **Zapiše se samo tisto, kar je vir POVEDAL, ne tisto, česar nismo slišali.**
 *
 * Vsak neuspeh zato nosi `definitive`. Dokler sta bila »postaja tega ne zna« in
 * »ta trenutek ni odgovora« isti izid, je kratek izpad omrežja postajo zapisal
 * kot `{ kind: 'none' }` — ta pa pomeni »ne poizveduj več«, torej ni bilo
 * pollov, ki bi napako opazili, in ne po ponovnem zagonu. Naslov in ovitek sta
 * obstala pri zadnji uspešni skladbi, na vseh platformah hkrati.
 */

import {
  azuracastNowPlayingUrl,
  azuracastShortcode,
  azuracastSseUrl,
  parseAzuracast,
  parseSseMessage,
} from '../../domain/azuracast';
import { pickIcecastSource, trackFromIcecast } from '../../domain/icecast-status';
import { parseStreamTitle, sameTrack, type Track } from '../../domain/icy-parse';
import {
  capabilityIsFresh,
  effectiveUrl,
  type MetaCapability,
  type Station,
} from '../../domain/stations';
import { icecastStatusUrl, icyApiUrl, metaApiUrl } from '../../domain/stream-url';
import type { NowPlayingListener, NowPlayingPort, StopWatching } from '../../ports/metadata';

/**
 * Icecast poll: 15 s je zgornji rob priporočila iz T3 §3. Krajši interval ne
 * kupi ničesar — postaje `title` osvežijo ob menjavi skladbe, ne pogosteje.
 */
const ICECAST_INTERVAL_MS = 15_000;
/**
 * ICY poll je dvakrat redkejši, ker je dvakrat dražji: vsak klic odpre pravo
 * povezavo s tokom in bere do prvega bloka. Icecast JSON je en GET.
 */
const ICY_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 10_000;
const REDETECT_AFTER_FAILURES = 3;
/**
 * Zaznava se ni izšla zaradi omrežja. Poskusi znova, a ne prehitro: en krog
 * zaznave so do trije zahtevki in vsak ima 10 s odmora. Krajši razmik bi
 * naslednji krog zagnal, preden bi se prejšnji sploh iztekel.
 */
const DETECT_RETRY_MS = 60_000;
/**
 * SSE ne poizveduje — pove postaja sama. Ta časovnik ni poll, ampak straža:
 * preveri, ali je povezava še živa (glej `SSE_STALE_MS`).
 */
const SSE_WATCHDOG_MS = 120_000;
/**
 * 🔴 Koliko tišine na SSE pomeni, da je povezava mrtva.
 *
 * Centrifugo pošilja utrip (`{}`) tudi takrat, ko se skladba ne menja, zato je
 * tišina merljiv znak. iOS ob uspavanju strani povezavo ubije **brez dogodka
 * `error`** — brez te meritve bi `EventSource` ostal videti odprt in postaja bi
 * obtičala pri zadnji skladbi za vedno.
 */
const SSE_STALE_MS = 90_000;

interface IcyResponse {
  title: string | null;
  supported: boolean;
}

/**
 * Ločuje »vir je odgovoril, skladbe pa ni« od »vira ni bilo«.
 *
 * 🔴 `definitive` je razlika med **»postaja tega ne zna«** in **»ta trenutek se
 * ni izšlo«**. Prvo se sme zapisati v `metaCapability`; drugo nikoli. Ko sta
 * bila oba en sam `FAILED`, je kratek izpad omrežja postajo trajno zapisal kot
 * `{ kind: 'none' }` — in `none` ne poizveduje več, tudi po ponovnem zagonu ne.
 */
type Read =
  | { ok: true; track: Track | null; artworkUrl?: string }
  | { ok: false; definitive: boolean };

/** Vir ni odgovoril: omrežje, odmor, 502, prekinjen krog. Minljivo. */
const TRANSIENT: Read = { ok: false, definitive: false };
/** Vir je odgovoril in povedal, da tega ne zna. Sme se zapisati. */
const UNSUPPORTED: Read = { ok: false, definitive: true };

/** Izid enega zahtevka; glej `fetchJson()`. */
type JsonRead =
  | { kind: 'json'; value: unknown }
  /** Strežnik je odgovoril, a ne s tem, kar rabimo — 404, 502, ne-JSON. */
  | { kind: 'rejected' }
  /** Odgovora ni bilo: omrežje, CORS, odmor, prekinjen krog. */
  | { kind: 'silent' };

const REJECTED: JsonRead = { kind: 'rejected' };
const SILENT: JsonRead = { kind: 'silent' };

/** Le toliko `EventSource`, kolikor kaskada rabi — da ga test lahko podtakne. */
export interface EventSourceLike {
  addEventListener(type: 'message', handler: (event: { data: string }) => void): void;
  addEventListener(type: 'error', handler: () => void): void;
  close(): void;
}

export interface MetadataCascadeOptions {
  /** Osnova za `/api/*`; testi jo podajo izrecno. */
  base?: string;
  /** Zamenljiv `fetch` — testi kaskado poženejo brez omrežja. */
  fetchImpl?: typeof fetch;
  /** Zamenljiv `EventSource`; `null` pomeni, da SSE ni na voljo. */
  eventSourceImpl?: ((url: string) => EventSourceLike) | null;
  /**
   * Naročnina na vrnitev v ospredje. Testi podajo svojo in kaskado s tem
   * poženejo brez DOM.
   */
  foregroundImpl?: (handler: () => void) => () => void;
  now?: () => number;
}

function defaultEventSource(url: string): EventSourceLike {
  return new EventSource(url);
}

/**
 * 🔴 **Naročnina na vrnitev aplikacije v ospredje.**
 *
 * Poll stoji na `setTimeout`, tega pa mobilni brskalniki v ozadju in ob
 * zaklenjenem zaslonu **zamrznejo**. Zvok teče naprej — to je bilo preverjeno
 * na napravi v fazi 1 — števec pa ne. Uporabnik zato ob pogledu na zaslon vidi
 * skladbo izpred zamrznitve, skupaj z njenim ovitkom, in bi brez tega na pravi
 * naslov čakal še do polnega intervala po odklepu.
 *
 * Prehod **v ozadje** se namenoma ne obravnava: `setTimeout`, ki se ne sproži,
 * ne stane ničesar, brskalnik pa ga ustavi sam.
 *
 * ⚠️ `pageshow` je zraven, ker iOS ob vrnitvi iz predpomnilnika strani
 * `visibilitychange` ne sproži vedno.
 */
function domForeground(handler: () => void): () => void {
  // Kaskada mora ostati preverljiva brez brskalnika (T1 §3), zato je DOM
  // neobvezen in ne pogoj.
  if (typeof document === 'undefined') return () => undefined;

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') handler();
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onVisible);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onVisible);
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function createMetadataCascade(options: MetadataCascadeOptions = {}): NowPlayingPort {
  const doFetch: typeof fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const now = options.now ?? (() => Date.now());
  const subscribeForeground = options.foregroundImpl ?? domForeground;
  const openSse =
    options.eventSourceImpl === undefined
      ? typeof EventSource === 'undefined'
        ? null
        : defaultEventSource
      : options.eventSourceImpl;

  /**
   * 🔴 Izid enega zahtevka ima **tri** stanja, ne dveh.
   *
   * »Strežnik je odgovoril, a ne s tem, kar rabimo« in »odgovora ni bilo« sta
   * bila prej oba `null`. Razlika je ključna: prvo pove, da vira ni, drugo pa
   * samo, da ta trenutek ne vemo. Ko sta bila enaka, je izpad omrežja postajo
   * zapisal kot »nima metapodatkov« — in tak zapis ne poizveduje več.
   */
  async function fetchJson(url: string, signal: AbortSignal): Promise<JsonRead> {
    let response: Response;
    try {
      response = await doFetch(url, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)]),
        // 🔴 Metapodatki se NIKOLI ne smejo brati iz predpomnilnika brskalnika.
        //
        // Ob zadetku `fetch()` sploh ne gre na omrežje: poll teče naprej, vrača
        // pa isti odgovor. Izmerjeno v produkciji — `Browser Cache TTL` na coni
        // je našo glavo `max-age=10` prepisal s **4 urami** in naslov skladbe je
        // obstal čez pet skladb (T4 §5).
        //
        // Worker glavo od zdaj postavlja pravilno, a to je nastavitev zunaj
        // repozitorija. Kar je tu, je pod našim nadzorom in velja ne glede nanjo.
        cache: 'no-store',
      });
    } catch {
      // Postaja brez CORS, brez odziva, odmor ali prekinjen krog. Ni napaka, ki
      // bi jo uporabnik smel videti — in tudi ni dokaz, da vira ni.
      return SILENT;
    }

    if (!response.ok) return REJECTED;
    try {
      return { kind: 'json', value: await response.json() };
    } catch {
      return REJECTED;
    }
  }

  return {
    watch(station: Station, listener: NowPlayingListener): StopWatching {
      const controller = new AbortController();
      const { signal } = controller;
      const streamUrl = effectiveUrl(station);
      const statusUrl = icecastStatusUrl(streamUrl);
      const shortcode = azuracastShortcode(streamUrl);
      const azuraNowPlaying =
        shortcode === null ? null : azuracastNowPlayingUrl(streamUrl, shortcode);
      const azuraSse = shortcode === null ? null : azuracastSseUrl(streamUrl, shortcode);

      let timer: ReturnType<typeof setTimeout> | undefined;
      let stopped = false;
      /**
       * 🔴 Zaporedna številka kroga in signal tekočega kroga.
       *
       * Ko iOS uspava nameščeno PWA, zamrzne tudi `fetch`, ki je takrat v teku;
       * ob vrnitvi se ta obljuba pogosto **nikoli ne razreši in ne zavrne**.
       * Zato ima vsak krog svoj signal, ki ga naslednji krog prekine, izid
       * starega kroga pa se zavrže po številki.
       */
      let generation = 0;
      let inflight: AbortController | null = null;
      let pollSignal: AbortSignal = signal;
      let capability: MetaCapability | null = capabilityIsFresh(station, now())
        ? (station.metaCapability ?? null)
        : null;
      /**
       * Ali Icecast JSON pride prek `/api/meta`.
       *
       * Prebere se iz shranjenega zapisa, ker je direktni poskus pri postaji
       * brez CORS **vedno** zavrnjen — in te zavrnitve ni mogoče utišati.
       * Brez tega jo je vsaka nova seja ponovila.
       */
      let viaProxy = capability?.kind === 'icecast-json' ? (capability.viaProxy ?? false) : false;
      let source: EventSourceLike | null = null;
      /** Kdaj je SSE nazadnje karkoli poslal — tudi utrip šteje. */
      let sseHeardAt = 0;
      /** SSE je odpovedal; ista postaja se od tu naprej poizveduje s pollom. */
      let sseFailed = false;
      let failures = 0;
      let last: Track | null = null;
      let announced = false;

      const emit = (track: Track | null, artworkUrl?: string): void => {
        // Ista skladba se ne naznani dvakrat — sicer bralnik zaslona ob vsakem
        // pollu ponovi isti naslov (P2 §3).
        if (announced && sameTrack(track, last)) return;
        announced = true;
        last = track;
        listener.onTrack(artworkUrl === undefined ? { track } : { track, artworkUrl });
      };

      const readAzuracast = async (): Promise<Read> => {
        // Oznake postaje ni v poti — to ni AzuraCast in nikoli ne bo.
        if (azuraNowPlaying === null) return UNSUPPORTED;
        const json = await fetchJson(azuraNowPlaying, pollSignal);
        if (json.kind === 'silent') return TRANSIENT;
        // AzuraCast pošilja `access-control-allow-origin: *` (izmerjeno na
        // šestih postavitvah), zato zavrnjen odgovor pomeni, da tam tega API-ja
        // ni — ne, da smo brez omrežja.
        if (json.kind === 'rejected') return UNSUPPORTED;

        // Strežnik je odgovoril, a odgovor ni oblika AzuraCasta: ta člen za to
        // postajo ne obstaja, kar je izid in ne izpad.
        const parsed = parseAzuracast(json.value);
        if (parsed === null) return UNSUPPORTED;
        return parsed.artworkUrl === undefined
          ? { ok: true, track: parsed.track }
          : { ok: true, track: parsed.track, artworkUrl: parsed.artworkUrl };
      };

      /**
       * Odgovor Icecasta, direktno ali prek `/api/meta`.
       *
       * ⚠️ **O izidu odloči proxy, ne direktna pot.** Direktni zahtevek pade
       * tiho tudi takrat, kadar postaja samo nima CORS — iz brskalnika sta
       * »ni strežnika« in »ni glave« neločljiva. Naš Worker glav ne potrebuje,
       * zato je njegov odgovor edino pričevanje, ki mu smemo verjeti.
       */
      const loadIcecast = async (url: string): Promise<JsonRead> => {
        if (!viaProxy) {
          // Icecast od 2.4.1 CORS pošilja privzeto — direktna pot je pravilo,
          // proxy izjema (T4 §5).
          const direct = await fetchJson(url, pollSignal);
          if (direct.kind === 'json') return direct;
        }

        const proxied = await fetchJson(metaApiUrl(url, options.base), pollSignal);
        if (proxied.kind !== 'json') return proxied;

        viaProxy = true;
        return proxied;
      };

      const readIcecast = async (url: string): Promise<Read> => {
        const json = await loadIcecast(url);
        if (json.kind === 'silent') return TRANSIENT;
        if (json.kind === 'rejected') return UNSUPPORTED;
        return { ok: true, track: trackFromIcecast(json.value, streamUrl) };
      };

      const readIcy = async (): Promise<Read> => {
        // ⚠️ `pollSignal`, ne `signal`: to je edini člen, ki ga naslednji krog
        // sicer ne bi prekinil, in ravno viseča poizvedba je tista, ki na iOS
        // ne konča nikoli.
        const read = await fetchJson(icyApiUrl(streamUrl, options.base), pollSignal);
        // Tudi zavrnjen odgovor je tu minljiv: `502 upstream_unreachable` pove,
        // da se Worker ta hip ni mogel povezati s tokom, ne da tok ICY nima.
        if (read.kind !== 'json') return TRANSIENT;

        const body = read.value as IcyResponse;
        // `supported: false` ni okvara — postaja ICY preprosto ne oddaja in ta
        // člen kaskade zanjo ne obstaja. To je edini dokončni izid tega člena:
        // Worker ga izpelje iz odsotnosti glave `icy-metaint`.
        if (!body.supported) return UNSUPPORTED;
        return { ok: true, track: body.title === null ? null : parseStreamTitle(body.title) };
      };

      /**
       * Kaskada od zgoraj navzdol; vrne prvo pot, ki kaj da.
       *
       * AzuraCast je prvi, ker je najcenejši za preverjanje — oznaka postaje
       * je razvidna iz naslova toka in en zahtevek pove vse.
       *
       * 🔴 **`null` pomeni »ta trenutek se ne da ugotoviti«, ne »ni vira«.**
       *
       * `{ kind: 'none' }` se vrne samo, kadar je vsak člen **odgovoril** in
       * povedal ne. Če je kdorkoli molčal — omrežje, odmor, 502, prekinjen krog
       * — je `none` ugibanje, ki bi ga zapis v `metaCapability` spremenil v
       * dejstvo za naslednjih 24 ur. Prav to je postajo ubilo: kratek izpad je
       * zapisal `none`, `none` ne poizveduje več, in poljubno število uspešnih
       * pollov po tem se ni nikoli zgodilo, ker pollov ni bilo.
       */
      const detect = async (): Promise<MetaCapability | null> => {
        let silent = false;

        if (azuraSse !== null) {
          const read = await readAzuracast();
          if (read.ok) return { kind: 'azuracast-sse', url: azuraSse };
          if (!read.definitive) silent = true;
        }
        if (stopped) return null;

        // HLS metapodatki pridejo iz `cuechange` na `TextTrack`, ne iz omrežja;
        // bralnik pride s predvajalnikom HLS v fazi 6 (D1).
        if (station.isHls) return { kind: 'hls-cue' };

        if (statusUrl !== null) {
          const json = await loadIcecast(statusUrl);
          // Odgovor mora vsebovati NAŠ vir. Icecast na gostitelju, ki oddaja
          // druge postaje, o naši ne pove nič in bi poll trajno vračal nič.
          if (json.kind === 'silent') silent = true;
          else if (json.kind === 'json' && pickIcecastSource(json.value, streamUrl) !== null) {
            // `viaProxy` je ta trenutek že znan: `loadIcecast()` ga je postavil,
            // ko je direktna pot padla. Zapiše se z zapisom, da naslednja seja
            // zavrnjenega zahtevka ne ponovi.
            return viaProxy
              ? { kind: 'icecast-json', url: statusUrl, viaProxy: true }
              : { kind: 'icecast-json', url: statusUrl };
          }
        }
        if (stopped) return null;

        const icy = await readIcy();
        if (icy.ok) return { kind: 'icy-proxy' };
        if (!icy.definitive) silent = true;

        return silent ? null : { kind: 'none' };
      };

      /**
       * Odpre SSE. Od tu naprej ni pollinga — menjavo skladbe pove postaja.
       *
       * Ob napaki se `EventSource` sam poskuša znova povezati; če to ne
       * obrodi, gremo na poll istega `/api/nowplaying`. Ta zamenjava je
       * enosmerna: ko poll teče, se SSE v isti seji ne poskuša znova.
       *
       * 🔴 **Napaka po prvi skladbi se ne sme prezreti.** Prej je pogoj
       * `announced` napako pogoltnil, `armNext()` za SSE ni naročil ničesar in
       * `startSse()` se je ob mrtvem, a nezaprtem `source` takoj vrnil —
       * postaja je obstala pri prvi skladbi do konca seje. Zadelo je natanko
       * tiste tri postaje, ki SSE dejansko imajo.
       */
      function startSse(url: string): void {
        if (source !== null) return;
        if (openSse === null) {
          sseFailed = true;
          armNext();
          return;
        }

        sseHeardAt = now();
        source = openSse(url);
        source.addEventListener('message', (event) => {
          if (stopped) return;
          // Utrip `{}` skladbe ne prinese, a dokazuje, da povezava živi —
          // zato se šteje pred razčlenjevanjem, ne za njim.
          sseHeardAt = now();
          const np = parseSseMessage(safeJson(event.data));
          if (np === null) return;

          const parsed = parseAzuracast(np);
          if (parsed !== null) emit(parsed.track, parsed.artworkUrl);
        });
        source.addEventListener('error', () => {
          if (stopped) return;
          source?.close();
          source = null;
          sseFailed = true;
          armNext();
        });
      }

      /**
       * Straža nad SSE: iOS povezavo ob uspavanju strani ubije **brez dogodka
       * `error`**, zato je edini dokaz o smrti tišina.
       *
       * Ne preklopi na poll — poskusi znova odpreti. SSE je za AzuraCast
       * cenejši in točnejši od pollinga; šele če tudi novi `EventSource` javi
       * napako, gre kaskada na poll.
       */
      function reviveSse(url: string): void {
        if (source !== null && now() - sseHeardAt > SSE_STALE_MS) {
          source.close();
          source = null;
        }
        startSse(url);
      }

      /**
       * 🔴 **Naslednji krog se naroči TAKOJ, ne po koncu tega.**
       *
       * Prvotna izvedba je `schedule()` klicala na koncu poizvedbe. Na iOS to
       * pomeni trajno zamrznitev: sistem ob prehodu v ozadje uspava stran,
       * `fetch` v teku pa se ob vrnitvi pogosto **nikoli ne razreši in ne
       * zavrne** — poizvedba torej nikoli ne konča, `schedule()` se nikoli ne
       * pokliče in veriga je mrtva. Zvok teče naprej, metapodatki obtičijo pri
       * zadnji uspešni skladbi.
       *
       * Prijavljeno z naprave 2026-07-20 in izmerjeno z obeh strani: na
       * Windowsu se je vrstica pravilno osvežila čez menjavo skladbe, na
       * iPhonu — v Safariju **in** v nameščeni PWA — je obstala.
       *
       * Zato: časovnik se naroči na začetku, prejšnja viseča poizvedba se
       * prekine, izid zapoznelega kroga pa se zavrže po zaporedni številki.
       */
      const poll = async (): Promise<void> => {
        if (stopped) return;

        armNext();

        inflight?.abort();
        const own = new AbortController();
        inflight = own;
        pollSignal = AbortSignal.any([signal, own.signal]);
        const mine = ++generation;

        try {
          await pollOnce(mine);
        } finally {
          if (inflight === own) inflight = null;
        }
      };

      const pollOnce = async (mine: number): Promise<void> => {
        if (stopped) return;

        if (capability === null) {
          const detected = await detect();
          // Krog je zastarel: medtem se je začel nov in ta izid ne velja več.
          if (stopped || mine !== generation) return;
          // Zaznava ni odgovorila. Nič se ne zapiše, nič se ne obupa —
          // časovnik teče naprej in naslednji krog poskusi znova.
          if (detected === null) {
            armNext();
            return;
          }
          capability = detected;
          listener.onCapability(capability);
          // Interval je odvisen od izida, časovnik pa je bil naročen, ko izida
          // še ni bilo. Zdaj ga je treba popraviti.
          armNext();
        }

        let read: Read;
        switch (capability.kind) {
          case 'azuracast-sse':
            if (!sseFailed) {
              reviveSse(capability.url);
              return;
            }
            read = await readAzuracast();
            break;
          case 'icecast-json':
            read = await readIcecast(capability.url);
            break;
          case 'icy-proxy':
            read = await readIcy();
            break;
          // HLS cue še nima bralnika; postaja do takrat naslova skladbe ne
          // kaže, kar je manj napačno od izmišljenega.
          default:
            return;
        }
        if (stopped || mine !== generation) return;

        if (read.ok) {
          failures = 0;
          emit(read.track, read.artworkUrl);
        } else {
          failures += 1;
          if (failures >= REDETECT_AFTER_FAILURES) {
            failures = 0;
            capability = null;
            viaProxy = false;
          }
          // Sposobnost se je morda spremenila; naslednji krog mora upoštevati
          // nov interval, zato se časovnik naroči znova.
          armNext();
        }
      };

      /**
       * Razmik do naslednjega kroga. `undefined` pomeni, da kroga ni.
       *
       * ⚠️ **Vsaka veja, ki vrne `undefined`, je trajna zaustavitev.** Doslej
       * sta bili taki dve preveč: `none` iz minljivega izpada in `azuracast-sse`
       * brez straže. Kaskada, ki se ne more sama vrniti, se ne vrne.
       */
      function nextInterval(): number | undefined {
        const kind = capability?.kind;
        // Zaznava še ni uspela — poskusi znova, redkeje od pollinga.
        if (kind === undefined) return DETECT_RETRY_MS;
        switch (kind) {
          // Cue prinese predvajalnik HLS (faza 6), ne omrežje.
          case 'hls-cue':
            return undefined;
          // Vsak člen je odgovoril in povedal ne. Ponovno preverjanje pokrije
          // `metaCheckedAt` (24 h za `none`), ne poll.
          case 'none':
            return undefined;
          case 'azuracast-sse':
            return sseFailed ? ICECAST_INTERVAL_MS : SSE_WATCHDOG_MS;
          case 'icy-proxy':
            return ICY_INTERVAL_MS;
          case 'icecast-json':
            return ICECAST_INTERVAL_MS;
        }
      }

      /** Naroči naslednji krog; sam po sebi ne poizveduje. */
      function armNext(): void {
        if (timer !== undefined) clearTimeout(timer);
        timer = undefined;
        if (stopped) return;

        const interval = nextInterval();
        if (interval === undefined) return;
        timer = setTimeout(() => void poll(), interval);
      }

      // Časovnika ni treba počistiti posebej — `poll()` ga prek `armNext()`
      // zamenja z novim. Pri SSE `poll()` ne poizveduje, ampak preveri, ali je
      // povezava tišino preživela (`reviveSse`).
      const unsubscribeForeground = subscribeForeground(() => {
        if (stopped) return;
        void poll();
      });

      void poll();

      return () => {
        stopped = true;
        if (timer !== undefined) clearTimeout(timer);
        unsubscribeForeground();
        source?.close();
        source = null;
        controller.abort();
      };
    },
  };
}
